import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CoffeeOutlined,
  CreditCardOutlined,
  ExperimentOutlined,
  LoadingOutlined,
  RocketOutlined,
  SaveOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import apiClient from "../../api/apiClient";
import { isCanceledApiRequest } from "../../api/apiRequestPolicy";
import {
  resolveContextStepPresentation,
  resolveOnboardingStepPresentation,
} from "./commercialOnboardingPresentation";
import "./CommercialOnboardingAssistant.css";

const { Paragraph, Text, Title } = Typography;

const STORAGE_PREFIX = "elsa:commercial-onboarding";

const profileOptions = [
  {
    key: "retail",
    title: "Achat-revente simple",
    subtitle: "Produits achetes, stockes puis revendus.",
    icon: <ShopOutlined />,
    tone: "blue",
  },
  {
    key: "production",
    title: "Production / transformation",
    subtitle: "Matières premières, configurations, coûts et produits finis.",
    icon: <ExperimentOutlined />,
    tone: "amber",
  },
  {
    key: "restaurant",
    title: "Restauration",
    subtitle: "Commandes, cuisine, recettes et menu digital.",
    icon: <CoffeeOutlined />,
    tone: "red",
  },
  {
    key: "ecommerce",
    title: "E-commerce",
    subtitle: "Boutique en ligne, fiches produit et commandes web.",
    icon: <RocketOutlined />,
    tone: "purple",
  },
  {
    key: "commercial_services",
    title: "Services commerciaux",
    subtitle: "Prestations, rendez-vous, clients et facturation.",
    icon: <TeamOutlined />,
    tone: "green",
  },
];

const goalOptions = [
  { key: "sell", label: "Vendre vite" },
  { key: "stock", label: "Maitriser le stock" },
  { key: "purchase", label: "Suivre les achats" },
  { key: "produce", label: "Produire / transformer" },
  { key: "online", label: "Vendre en ligne" },
  { key: "cash", label: "Suivre caisse et tresorerie" },
  { key: "reports", label: "Piloter avec les rapports" },
];

const defaultGoalsByProfile = {
  retail: ["sell", "stock", "purchase", "cash"],
  production: ["produce", "stock", "purchase", "reports"],
  restaurant: ["sell", "produce", "stock", "online"],
  ecommerce: ["online", "stock", "sell", "reports"],
  commercial_services: ["sell", "cash", "reports"],
};

const posCashoutPrerequisites = [
  {
    title: "Définir la devise de référence de l'entreprise",
    detail:
      "La devise de référence doit être rattachée à l'entreprise, pas seulement créée dans la liste des devises.",
    path: "/gestion-entreprises",
    blocking: true,
    doneWhen: "companyCurrency",
  },
  {
    title: "Créer le compte de trésorerie de l'établissement",
    detail:
      "Le POS doit trouver un compte commercial actif et compatible avec la devise de référence.",
    path: "/caisse-banques",
    blocking: true,
    doneWhen: "treasuryAccount",
  },
  {
    title: "Créer les modes de paiement",
    detail:
      "Un encaissement POS payé exige au moins un mode de paiement disponible.",
    path: "/gestion-mode-paiement",
    blocking: true,
    doneWhen: "paymentModes",
  },
  {
    title: "Rattacher chaque mode à un compte",
    detail:
      "Chaque mode doit avoir un routage POS ou un compte par défaut actif du même type.",
    path: "/gestion-magasin",
    blocking: true,
    doneWhen: "paymentRouting",
  },
];

const guideByProfile = {
  retail: {
    summary:
      "Le meilleur demarrage consiste a structurer le catalogue, poser le stock initial, preparer l'encaissement POS, puis lancer la vente.",
    steps: [
      {
        title: "Creer les familles, unites et produits",
        detail: "Base indispensable pour les achats, le stock, la vente et la caisse.",
        path: "/gestion-produits",
      },
      {
        title: "Renseigner le stock initial",
        detail: "Permet d'eviter les ventes sans quantite fiable des le depart.",
        path: "/gestion-stock",
      },
      {
        title: "Configurer les fournisseurs et les achats",
        detail: "Les achats alimentent le stock et les couts d'achat.",
        path: "/gestion-achats",
      },
      ...posCashoutPrerequisites,
      {
        title: "Faire la premiere vente POS",
        detail:
          "Tester un panier avec paiement apres avoir valide devise, compte, mode de paiement et routage.",
        path: "/gestion-pos",
      },
    ],
  },
  production: {
    summary:
      "On commence par les matières premières et les produits finis, puis les configurations de production et le suivi des coûts.",
    steps: [
      {
        title: "Créer les matières premières et produits finis",
        detail: "Distinguer matières premières, semi-finis et produits finis.",
        path: "/gestion-produits",
      },
      {
        title: "Configurer les unités de production",
        detail: "Définir les configurations, quantités de sortie et coûts variables.",
        path: "/production/units",
      },
      {
        title: "Enregistrer les achats de matières premières",
        detail: "Les consommations de production s'appuient sur un stock propre.",
        path: "/gestion-achats",
      },
      {
        title: "Lancer une premiere production",
        detail: "Verifier le cout de revient et la sortie en stock.",
        path: "/production/process",
      },
    ],
  },
  restaurant: {
    summary:
      "Le parcours restauration relie produits, recettes, commandes salle, menu digital, encaissement POS et file cuisine.",
    steps: [
      {
        title: "Initialiser les recettes de base",
        detail: "Utiliser les packs ou suggestions de production pour accelerer la configuration.",
        path: "/production/units",
      },
      {
        title: "Configurer le menu digital",
        detail: "Publier les plats vendables et preparer la prise de commande.",
        path: "/gestion-ventes/menu-digital",
      },
      ...posCashoutPrerequisites,
      {
        title: "Tester une prise de commande payee",
        detail:
          "Valider le flux salle, ticket cuisine, statut de preparation et encaissement.",
        path: "/gestion-ventes/commandes-restaurant",
      },
      {
        title: "Suivre la file cuisine",
        detail: "Transformer les commandes en production lorsque la recette est disponible.",
        path: "/production/kitchen",
      },
    ],
  },
  ecommerce: {
    summary:
      "Le demarrage e-commerce doit relier produits, stock, boutique publique et traitement des commandes.",
    steps: [
      {
        title: "Configurer la boutique",
        detail: "Definir l'identite de la boutique, le lien public et les parametres de vente.",
        path: "/gestion-ecommerce",
      },
      {
        title: "Preparer les fiches produits",
        detail: "Publier seulement les articles avec prix, images et stock coherent.",
        path: "/gestion-ecommerce",
      },
      {
        title: "Verifier le stock synchronise",
        detail: "Le stock commercial reste la source de verite pour les commandes web.",
        path: "/gestion-stock",
      },
      {
        title: "Traiter une commande en ligne",
        detail: "Controler la reception, le paiement et la preparation.",
        path: "/gestion-ecommerce",
      },
    ],
  },
  commercial_services: {
    summary:
      "Pour les services, il faut d'abord creer les prestations, structurer les clients, preparer l'encaissement puis convertir en vente.",
    steps: [
      {
        title: "Creer les services vendus",
        detail: "Chaque service peut ensuite etre facture ou lie a un rendez-vous.",
        path: "/gestion-services",
      },
      {
        title: "Configurer les clients",
        detail: "Centraliser les contacts, soldes et conditions commerciales.",
        path: "/gestion-entites",
      },
      {
        title: "Planifier les rendez-vous",
        detail: "Suivre les prestations a realiser avant facturation.",
        path: "/gestion-rendez-vous",
      },
      {
        title: "Preparer l'encaissement",
        detail:
          "Configurer devise, compte de tresorerie et mode de paiement avant les paiements entrants.",
        path: "/gestion-mode-paiement",
      },
      {
        title: "Facturer et encaisser",
        detail: "Transformer le travail realise en vente puis suivre le paiement.",
        path: "/gestion-ventes",
      },
    ],
  },
};

const profileMap = profileOptions.reduce((acc, option) => {
  acc[option.key] = option;
  return acc;
}, {});

const normalizeProfile = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return profileMap[normalized] ? normalized : "";
};

const buildStorageKey = (companyId, suffix) =>
  `${STORAGE_PREFIX}:${companyId || "unknown"}:${suffix}`;

const normalizeContextId = (value) => {
  const candidate = value?.id ?? value;
  const parsed = Number(candidate);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const CommercialOnboardingAssistant = ({
  open,
  user,
  workspaceReady,
  selectedCompany,
  selectedWarehouse,
  onClose,
  onNavigate,
  onProfileSaved,
}) => {
  const company = user?.company || {};
  const companyId =
    normalizeContextId(selectedCompany) ||
    normalizeContextId(company.id) ||
    normalizeContextId(user?.company_id);
  const warehouseId = normalizeContextId(selectedWarehouse);
  const storedProfile = normalizeProfile(company.activity_profile);
  const [selectedProfile, setSelectedProfile] = useState(
    storedProfile || "retail"
  );
  const [selectedGoals, setSelectedGoals] = useState(defaultGoalsByProfile.retail);
  const [saving, setSaving] = useState(false);
  const [readinessState, setReadinessState] = useState({
    status: "idle",
    data: null,
    error: "",
  });

  useEffect(() => {
    if (!open) return;

    const initialProfile = storedProfile || "retail";
    setSelectedProfile(initialProfile);
    setSelectedGoals(defaultGoalsByProfile[initialProfile] || []);
  }, [open, storedProfile]);

  const selectedProfileOption = profileMap[selectedProfile] || profileOptions[0];
  const selectedGuide = guideByProfile[selectedProfile] || guideByProfile.retail;
  const firstActionPath = selectedGuide.steps.find((step) => step.path)?.path;

  useEffect(() => {
    if (!open) {
      setReadinessState({ status: "idle", data: null, error: "" });
      return undefined;
    }

    if (!workspaceReady || !companyId || !warehouseId) {
      setReadinessState({ status: "idle", data: null, error: "" });
      return undefined;
    }

    const controller = new AbortController();
    let isCurrentRequest = true;
    setReadinessState({ status: "loading", data: null, error: "" });

    const loadReadiness = async () => {
      try {
        const response = await apiClient.get(
          `/api/warehouses/${warehouseId}/commercial-onboarding-status`,
          {
            params: { company_id: companyId },
            signal: controller.signal,
            skipGlobalLoading: true,
          }
        );
        const data = response.data?.data;
        if (!response.data?.success || !data) {
          throw new Error("Réponse de vérification invalide");
        }
        if (!isCurrentRequest) return;
        setReadinessState({ status: "success", data, error: "" });
      } catch (error) {
        if (!isCurrentRequest || isCanceledApiRequest(error)) return;
        setReadinessState({
          status: "error",
          data: null,
          error:
            error?.response?.data?.error ||
            "Impossible de vérifier la préparation du POS.",
        });
      }
    };

    void loadReadiness();
    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [
    companyId,
    open,
    warehouseId,
    workspaceReady,
  ]);

  const planSteps = useMemo(() => {
    const contextPresentation = resolveContextStepPresentation({
      workspaceReady,
      readinessStatus: readinessState.status,
      readiness: readinessState.data,
      companyId,
      warehouseId,
    });
    const contextStep = {
      title: "Activer le contexte de travail",
      path: null,
      ...contextPresentation,
    };

    const guidedSteps = selectedGuide.steps.map((step) => {
      const presentation = resolveOnboardingStepPresentation({
        step,
        readinessStatus: readinessState.status,
        readiness: readinessState.data,
        workspaceReady,
      });
      return { ...step, ...presentation };
    });

    return [contextStep, ...guidedSteps];
  }, [
    companyId,
    readinessState.data,
    readinessState.status,
    selectedCompany,
    selectedGuide.steps,
    selectedWarehouse,
    workspaceReady,
  ]);

  const toggleGoal = (goalKey) => {
    setSelectedGoals((current) =>
      current.includes(goalKey)
        ? current.filter((item) => item !== goalKey)
        : [...current, goalKey]
    );
  };

  const handleSelectProfile = (profileKey) => {
    setSelectedProfile(profileKey);
    setSelectedGoals(defaultGoalsByProfile[profileKey] || []);
  };

  const persistLocalChoices = () => {
    if (!companyId) return;

    localStorage.setItem(
      buildStorageKey(companyId, "choices"),
      JSON.stringify({
        profile: selectedProfile,
        goals: selectedGoals,
        updatedAt: new Date().toISOString(),
      })
    );
  };

  const markDismissed = () => {
    if (!companyId) return;
    localStorage.setItem(buildStorageKey(companyId, "dismissed"), "true");
  };

  const handleCancel = () => {
    persistLocalChoices();
    markDismissed();
    onClose?.();
  };

  const saveProfile = async ({ showSuccess = false } = {}) => {
    if (!companyId) {
      message.error("Entreprise introuvable pour enregistrer le profil.");
      return false;
    }

    await apiClient.put(`/api/companies/${companyId}`, {
      activity_profile: selectedProfile,
    });
    persistLocalChoices();
    localStorage.removeItem(buildStorageKey(companyId, "dismissed"));

    if (showSuccess) {
      message.success("Profil d'activite enregistre.");
    }

    await onProfileSaved?.();
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveProfile({ showSuccess: true });
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible d'enregistrer le profil d'activite."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleNavigate = async (path) => {
    setSaving(true);
    try {
      if (storedProfile !== selectedProfile) {
        const saved = await saveProfile();
        if (!saved) return;
      } else {
        persistLocalChoices();
      }

      markDismissed();
      onClose?.();
      if (path) {
        onNavigate?.(path);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible de preparer ce parcours."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      width={980}
      title="Assistant de demarrage commercial"
      className="commercial-onboarding"
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <Button key="close" onClick={handleCancel}>
          Fermer
        </Button>,
        <Button
          key="save"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          Enregistrer le profil
        </Button>,
        <Button
          key="start"
          type="primary"
          loading={saving}
          disabled={!firstActionPath}
          onClick={() => handleNavigate(firstActionPath)}
        >
          Commencer
        </Button>,
      ]}
    >
      <div className="commercial-onboarding__layout">
        <section className="commercial-onboarding__panel commercial-onboarding__panel--lead">
          <div>
            <Text className="commercial-onboarding__eyebrow">
              Qualification
            </Text>
            <Title level={4} className="commercial-onboarding__title">
              Quel parcours ressemble le plus a votre activite ?
            </Title>
            <Paragraph className="commercial-onboarding__copy">
              Le choix ajuste les prochaines etapes, les raccourcis proposes et
              les modules prioritaires pour la gestion commerciale.
            </Paragraph>
          </div>

          <div className="commercial-onboarding__profiles" role="radiogroup">
            {profileOptions.map((profile) => {
              const selected = selectedProfile === profile.key;

              return (
                <button
                  key={profile.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`commercial-onboarding__profile commercial-onboarding__profile--${profile.tone} ${
                    selected ? "commercial-onboarding__profile--selected" : ""
                  }`}
                  onClick={() => handleSelectProfile(profile.key)}
                >
                  <span className="commercial-onboarding__profile-icon">
                    {profile.icon}
                  </span>
                  <span className="commercial-onboarding__profile-copy">
                    <strong>{profile.title}</strong>
                    <small>{profile.subtitle}</small>
                  </span>
                  {selected && (
                    <CheckCircleOutlined className="commercial-onboarding__profile-check" />
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="commercial-onboarding__panel">
          <Text className="commercial-onboarding__eyebrow">Priorites</Text>
          <Title level={5} className="commercial-onboarding__subtitle">
            Objectifs du demarrage
          </Title>
          <div className="commercial-onboarding__goals">
            {goalOptions.map((goal) => (
              <label key={goal.key} className="commercial-onboarding__goal">
                <Checkbox
                  checked={selectedGoals.includes(goal.key)}
                  onChange={() => toggleGoal(goal.key)}
                />
                <span>{goal.label}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="commercial-onboarding__panel commercial-onboarding__panel--plan">
          <div className="commercial-onboarding__plan-head">
            <div>
              <Text className="commercial-onboarding__eyebrow">
                Plan recommande
              </Text>
              <Title level={5} className="commercial-onboarding__subtitle">
                {selectedProfileOption.title}
              </Title>
            </div>
            {storedProfile && storedProfile === selectedProfile ? (
              <Tag color="success">Profil actif</Tag>
            ) : (
              <Tag color="processing">A enregistrer</Tag>
            )}
          </div>

          <Alert
            type="info"
            showIcon
            message={selectedGuide.summary}
            className="commercial-onboarding__alert"
          />

          <div className="commercial-onboarding__steps">
            {planSteps.map((step, index) => (
              <div
                key={`${step.title}-${index}`}
                className={`commercial-onboarding__step ${
                  step.done ? "commercial-onboarding__step--done" : ""
                }`}
              >
                <span className="commercial-onboarding__step-index">
                  {step.done ? (
                    <CheckCircleOutlined />
                  ) : step.tag?.label === "Vérification" ? (
                    <LoadingOutlined spin />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="commercial-onboarding__step-copy">
                  <strong>{step.title}</strong>
                  {step.tag && (
                    <Tag
                      color={step.tag.color}
                      className="commercial-onboarding__step-tag"
                    >
                      {step.tag.label}
                    </Tag>
                  )}
                  <small>{step.detail}</small>
                </span>
                {step.path && (
                  <Button
                    size="small"
                    type={index === 1 ? "primary" : "default"}
                    disabled={saving}
                    onClick={() => handleNavigate(step.path)}
                  >
                    {step.actionLabel || "Ouvrir"}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <Space wrap className="commercial-onboarding__tags">
            <Tag icon={<AppstoreOutlined />}>Catalogue</Tag>
            <Tag icon={<ShopOutlined />}>Stock</Tag>
            <Tag icon={<BankOutlined />}>Tresorerie</Tag>
            <Tag icon={<CreditCardOutlined />}>Paiements POS</Tag>
            <Tag icon={<RocketOutlined />}>Canaux de vente</Tag>
          </Space>
        </section>
      </div>
    </Modal>
  );
};

export default CommercialOnboardingAssistant;
